import { useEffect, useRef, type ReactNode } from 'react'
import './Popover.css'

export interface PopoverProps {
  label: string
  triggerContent: ReactNode
  open: boolean
  onOpenChange: (open: boolean) => void
  children: ReactNode
  /** A plain count (`SortMenu`/`FilterPanel`) OR a pre-formatted cap string (e.g. `"9+"` — the Jobs
   * tray's running-count badge, IB-27 S2) — rendered verbatim, so the cap decision stays with the
   * caller who knows its own domain-specific cap threshold. */
  badge?: number | string | undefined
  align?: 'start' | 'end' | undefined
  width?: number | undefined
  triggerClassName?: string | undefined
}

/**
 * Non-modal disclosure popover (ProvenancePopover pattern, design-system.md
 * §3.7): `role="dialog"`, Esc closes, click-outside closes, focus returns to
 * the trigger on close. Backs FilterBuilder / ColumnManager / SortMenu /
 * SavedViews / ExportMenu — every list-control dropdown shares this shell.
 */
export function Popover({
  label,
  triggerContent,
  open,
  onOpenChange,
  children,
  badge,
  align = 'start',
  width = 280,
  triggerClassName = 'iw-chip',
}: PopoverProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    if (!open) return
    function handlePointerDown(event: MouseEvent) {
      if (!containerRef.current?.contains(event.target as Node)) {
        onOpenChange(false)
      }
    }
    function handleKeyDown(event: globalThis.KeyboardEvent) {
      if (event.key === 'Escape') {
        onOpenChange(false)
        triggerRef.current?.focus()
      }
    }
    document.addEventListener('mousedown', handlePointerDown)
    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('mousedown', handlePointerDown)
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [open, onOpenChange])

  return (
    <div className="iw-popover-container" ref={containerRef}>
      <button
        ref={triggerRef}
        type="button"
        className={triggerClassName}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={label}
        onClick={() => onOpenChange(!open)}
      >
        {triggerContent}
        {badge ? <span className="iw-popover-badge">{badge}</span> : null}
      </button>
      {open ? (
        <div
          className={`iw-popover-panel iw-popover-panel--${align}`}
          role="dialog"
          aria-label={label}
          style={{ width }}
        >
          {children}
        </div>
      ) : null}
    </div>
  )
}
